import Tender                 from '@/domains/Tenders/Tender';
import type Demand          from '@/domains/Demand/Demand';
import type {MismatchItem}  from '@/core/services/Mismatch';
import Offer                 from '@/domains/Offers/Offer';
import OfferCharacteristic from '@/domains/Offers/OfferCharacteristic';
import type Person          from '@/domains/Persons/Person';
import Decimal              from 'decimal.js';
import TYPES                from '@/core/services/Changelogs/types';
import Title_ru_RU          from '@/core/misc/decorators/Title_ru_RU';
import Title_ru_RU_Class    from '@/core/misc/decorators/Title_ru_RU_Class';
import serializeSimplified  from '@/core/misc/decorators/SerializeSimplified';
import Core                 from '@/core/Core';
import Price                from '@/core/types/Price';
import Qty                  from '@/core/types/Qty';
import VatRate              from '@/core/types/VatRate';
import Multiplier           from '@/core/types/Multiplier';
import DateRange            from '@/core/types/DateRange';
import DateSimple           from '@/core/types/DateSimple';
import AbstractOrderOption  from '@/domains/Abstract/AbstractOrderOption';
import PaymentTerm          from '@/domains/PaymentTerms/PaymentTerm';
import PaymentTermsService  from '@/domains/PaymentTerms/PaymentTermsService';
import DemandsService       from '@/domains/Demand/DemandsService';
import DeliveryType         from '@/domains/DeliveryTypes/DeliveryType';

import type Store                  from '@/domains/Stores/Store';

@Title_ru_RU_Class('Ставка тендера')
class TenderBid extends AbstractOrderOption {

  /** Минимальный шаг снижения цены, в долларах.
   *
   * Почему не фиксированный шаг 0.001 в валюте торгов?
   * Для валют с высокой инфляцией (UZS, VES) шаг 0.001 — это ничто.
   *
   * Конвертируя шаг из USD, мы получаем одинаковую реальную стоимость
   * снижения независимо от валюты торгов:
   *
   * Приведение к RUB (1 USD = 90 RUB):
   * | Валюта | Шаг (= 0.001 USD) | Было    | Стало   | В RUB было | В RUB стало |
   * |--------|-------------------|---------|---------|------------|-------------|
   * | USD    | 0.001             | 100.000 | 99.999  | 9000.00    | 8999.91     |
   * | EUR    | 0.00092           | 100.000 | 99.999  | 9800.00    | 9799.91     |
   * | UZS    | 12.5              | 100.000 | 87.5    | 0.72       | 0.63        |
   *
   * Приведение к INR (1 USD = 83 INR):
   * | Валюта | Шаг (= 0.001 USD) | Было    | Стало   | В INR было | В INR стало |
   * |--------|-------------------|---------|---------|------------|-------------|
   * | USD    | 0.001             | 100.000 | 99.999  | 8300.00    | 8299.917    |
   * | EUR    | 0.00092           | 100.000 | 99.999  | 9028.00    | 9027.917    |
   * | UZS    | 12.5              | 100.000 | 87.5    | 0.66       | 0.58        |
   *
   * Приведение к CNY (1 USD = 7.2 CNY):
   * | Валюта | Шаг (= 0.001 USD) | Было    | Стало   | В CNY было | В CNY стало |
   * |--------|-------------------|---------|---------|------------|-------------|
   * | USD    | 0.001             | 100.000 | 99.999  | 720.00     | 719.993     |
   * | EUR    | 0.00092           | 100.000 | 99.999  | 783.36     | 783.353     |
   * | UZS    | 12.5              | 100.000 | 87.5    | 0.058      | 0.050       |
   */
  static readonly BASE_STEP_USD = '0.001';

  /** Валюта, в которой задан минимальный шаг снижения цены */
  static readonly BASE_STEP_CURRENCY_CODE = 'USD';

  /** Ставка НДС по умолчанию, когда в офере она не указана.
   *  //TODO хардкод перенесён из прежнего кода — по-хорошему берётся из
   *  категории или системы налогообложения продавца. */
  static readonly DEFAULT_VAT_RATE = VatRate.fromServer(20);

  /** В какой тендер ставка */
  @serializeSimplified
  public tender: Tender;

  /** Ответственный за ставку с точки зрения поставщика */
  @serializeSimplified
  public responsibleSeller: Person | null = Core.currentUser;

  /** Офер нужен только для характеристик */
  @Title_ru_RU('предложение', TYPES.OBJECT)
  @serializeSimplified
  public offer: Offer | null;

  /** Склад поставщика редактируется, но не обнуляется: приходит из офера,
   *  контрол позволяет только заменить его на другой. это значит not null */
  declare protected _sellerStore: Store;

  /** Характеристики предложения по которым есть расхождения с продуктом */
    // TODO: типизировать массив id характеристик когда появится тип
  public mismatchingCharacteristics: any[] | null = null;

  /** ------------- Декораторы для переопределения титулов предков ------------- */
    // Часть полей унаследована от AbstractOrderOption, но имеют свой человеческий титул
    // именно в контексте ставки — переопределяем их здесь.

  @Title_ru_RU('цена продавца', TYPES.TENDER_PRICE)
  declare protected _priceSeller: Price;

  @Title_ru_RU('полная цена продавца', TYPES.TENDER_PRICE)
  declare public priceFullSeller: Price;

  @Title_ru_RU('НДС поставщика', TYPES.NUMBER)
  declare protected _vatRateSeller: VatRate | null;

  @Title_ru_RU('метод начисления НДС поставщика', TYPES.BOOLEAN, (oldValue: boolean, newValue: boolean) => {
    const gate: Record<string, string> = {
      true:  'включено',
      false: 'сверху',
    };

    return `с ${gate[String(oldValue)]} на ${gate[String(newValue)]}`;
  })
  declare protected _isVatIncludedSeller: boolean | null;

  @Title_ru_RU('полная цена покупателя', TYPES.TENDER_PRICE)
  declare public priceFullBuyer: Price | null;

  @Title_ru_RU('валюта предложения', TYPES.STRING)
  declare protected _offerCurrencyCode: string;

  @Title_ru_RU('условия оплаты', TYPES.PAYMENT_TERM)
  declare protected _paymentTerm: PaymentTerm;

  @Title_ru_RU('наценка категории', TYPES.NUMBER_PERCENT)
  declare public priceMod: Multiplier;

  @Title_ru_RU('полная цена покупателя c отсрочкой', TYPES.TENDER_PRICE)
  declare public priceFullBuyerWithDeferredPayment: Price | null;

  @Title_ru_RU('кредитная наценка', TYPES.NUMBER_PERCENT)
  declare public priceModDeferredPayment: Multiplier;

  @Title_ru_RU('дата оплаты поставщику', TYPES.DATE)
  declare public plannedPaymentDateToSeller: DateSimple;

  @Title_ru_RU('дата оплаты покупателя', TYPES.DATE)
  declare public plannedPaymentDateByBuyer: DateSimple;

  @Title_ru_RU('период отгрузки', TYPES.DATE_RANGE)
  declare protected _shipmentPeriod: DateRange;

  @Title_ru_RU('отметка доставка платформой', TYPES.CUSTOM_BOOLEAN)
  declare protected _isDeliveryNeeded: boolean | null;

  @Title_ru_RU('тип доставки', TYPES.OBJECT)
  declare protected _deliveryType: DeliveryType;

  @Title_ru_RU('особые условия перевозки', TYPES.STRING)
  declare protected _storeRulesText: string | null;

  static isTenderBid(value: unknown): value is TenderBid {
    return value instanceof TenderBid;
  }

  /**
   * Дата курса валюты — для ставки это начало тендера.
   * Симметрично с бэком: TenderBid::getDateForCurrencyRate() → new Timestamp($this->tender->startAt).
   */
  getDateForCurrencyRate(): DateSimple {
    return new DateSimple(this.tender.startAt);
  }

  /**
   * По задаче из Планфикса даты доставки берутся из тендера, а не из
   * предложения: поставщики забывают менять их сами.
   */
  getDesiredShipmentPeriod(): DateRange | null {
    if (Core.utils.isNullOrUndefined(this.tender) === true) {
      // тендера ещё нет — ставка собирается в fromOffer, setTender впереди
      return null;
    }
    else if (Core.utils.isNullOrUndefined(this._deliveryDays) === true) {
      // дни доставки не заданы, поставщик везёт сам — вычитать нечего, ждём ввода значения
      return null;
    }
    else if (
      Core.utils.isNullOrUndefined(this.tender.preorderShipmentPeriod) === false
      && this.tender.preorderShipmentPeriod!.isFilled()
    ) {
      /** Покупатель задал интервал сам (при предзаказе, и при закупке без
       *  обязательств выкупа) — сдвигаем обе границы. addDays возвращает
       *  новый диапазон, тендерный не мутируется. */
      return this.tender.preorderShipmentPeriod!.addDays(-this._deliveryDays!);
    }
    else if (Core.utils.isNullOrUndefined(this.tender.desiredReceiptDate) === true) {
      // если у тендера нет ждд, то считать вообще нечего
      return null;
    }
    else {
      // считаем первый валидный день и делаем из него рэндж
      const tenderDesiredShipmentDate = this.tender.desiredReceiptDate!.subtract({ days: this._deliveryDays });

      return new DateRange(tenderDesiredShipmentDate, tenderDesiredShipmentDate);
    }
  }

  /** Порядок присваиваний повторяет порядок объявления полей. */
  static fromServer(data: any, result: TenderBid = new TenderBid()): TenderBid {
    super.fromServer(data, result);

    // мы не можем гидрировать весь офер, так как он приходит обрезанным. но нам весь офер и не нужен. нам надо только характеристики
    //а также для реестра тендеров информация об офере не требуется, поэтому с бэка приходит null, данная проверка
    //защищает от попытки записать свойство в null
    if (result.offer !== null) {
      result.offer.characteristics = Core.utils.hydrateArray(result.offer.characteristics, OfferCharacteristic.fromServer);
    }

    result._priceSeller     = Core.utils.hydrate(data.priceSeller, Price.fromServer)!;
    result.priceFullSeller  = Core.utils.hydrate(data.priceFullSeller, Price.fromServer)!;
    result.priceFullBuyer   = Core.utils.hydrate(data.priceFullBuyer, Price.fromServer);

    result._paymentTerm = Core.utils.hydrate(data.paymentTerm, PaymentTerm.fromServer)!;

    result.priceFullBuyerWithDeferredPayment = Core.utils.hydrate(data.priceFullBuyerWithDeferredPayment, Price.fromServer);

    result.plannedPaymentDateToSeller = Core.utils.hydrate(data.plannedPaymentDateToSeller, DateSimple.fromServer)!;
    result.plannedPaymentDateByBuyer  = Core.utils.hydrate(data.plannedPaymentDateByBuyer, DateSimple.fromServer)!;

    result._shipmentPeriod       = Core.utils.hydrate(data.shipmentPeriod, DateRange.fromServer);
    result.productExpirationDate = Core.utils.hydrate(data.productExpirationDate, DateSimple.fromServer);

    result.priceModDeferredPayment = Core.utils.hydrate(data.priceModDeferredPayment, Multiplier.fromServer)!;

    /** FIXME нужно узнать, как это значение живёт на беке: отдаётся ли
     *  receiptPeriod для ставки вообще. Пока не отдаётся — считаем сами,
     *  иначе интервал получения останется пустым. */
    if (Core.utils.isNullOrUndefined(result.receiptPeriod) && result._shipmentPeriod?.isFilled()) {
      result.receiptPeriod = result.shipmentPeriod.addDays(result.deliveryDays!);
    }

    // result.tender Инициализируется в Tender

    return result;
  }

  /**
   * Создание ставки из офера.
   * Проверяет наличие транспортных зон у складов.
   *
   * Поля, зависящие от тендера, здесь не заполняются — сразу следом
   * вызывается setTender.
   */
  static fromOffer(offer: Offer, result: TenderBid = new TenderBid()): TenderBid {
    super.fromOffer(offer, result);

    /** Ставку со складом покупателя без транспортной зоны создать нельзя:
     *  склад покупателя в форме ставки не редактируется, исправить это
     *  пользователю нечем. */
    const buyerStoreError = offer.buyerStore.validateTransportZone();

    if (buyerStoreError !== null) {
      const error = new Error(buyerStoreError);

      Core.logException(error);

      throw error;
    }

    result.offer = offer;

    result.sellersCode = result.offer!.sellersCode;

    result.shipmentPeriod = result.offer.shipmentPeriod;

    result.plannedPaymentDateToSeller = offer.plannedPaymentDateToSeller;
    result.plannedPaymentDateByBuyer  = offer.plannedPaymentDateByBuyer;

    /** Копируем как есть: нужны эти поля или нет — решает тендер, но сами
     *  значения от него не зависят. */
    result.preorderMinQty         = offer.preorderMinQty;
    result.preorderMonthlyReserve = offer.preorderMonthlyReserve;

    /** Ставка — производная офера: период получения берём его, свой расчёт
     *  дал бы то же самое. */
    result.receiptPeriod = offer.receiptPeriod;

    return result;
  }

  setTender(tender: Tender): void {
    this.tender = tender;

    this.calculateShipmentPeriod();

    /** НДС покупателя берётся из категории тендера — до проверки ниже. */
    this.vatRateBuyer = tender.productCategory.vatRate;

    if (
      this._vatRateSeller
      && this.vatRateBuyer
      && this._vatRateSeller.greaterThan(this.vatRateBuyer)
    ) {
      Core.showNotification.error(
        `Ошибка расчета цены: НДС поставщика ${this._vatRateSeller} выше НДС покупателя ${this.vatRateBuyer}`,
      );
      throw new Error();
    }

    /** Условия оплаты уже пришли из офера в fromOffer. Своё умолчание
     *  ставим только когда в офере их не было — отсрочка берётся из
     *  категории тендера. */
    if (this.offer!.paymentTerm) {
      // условия оплаты пришли из офера — умолчание не ставим
    }
    else {
      this._paymentTerm = new PaymentTerm(
        PaymentTermsService.getEventById('after_receiving'),
        tender.productCategory.paymentDelay,
      );
    }

    /** Доступное количество берём из офера, а если он его не несёт — из
     *  потребности тендера. Второй путь асинхронный: поле заполнится уже
     *  после того, как setTender вернёт управление. */
    if (this.offer!.qtyAvailable && this.offer!.qtyAvailable.isPositive()) {
      this.qtyAvailable = this.offer!.qtyAvailable;
    }
    else {
      DemandsService.get(tender.demand.id)
        .then((demand: any) => {
          this.qtyAvailable = demand.qtyDesired;
        });
    }

    /** С появлением deliveryPricePerUnit мы больше не учитываем кэф priceModTransport */
    this.priceMod = tender.productCategory.priceModProfit;
  }

  /**
   * Самая конкурентная цена продавца: на минимальный шаг ниже текущей
   * лучшей ставки, а если ставок нет — ниже стартовой цены тендера.
   *
   * Геттер, а не поле: значение зависит от цен, НДС, наценок и цены
   * доставки — любое их изменение пересчитывает результат само, звать
   * ничего не нужно.
   *
   * Курсы берутся синхронно из кеша. Если их там нет — регистрируем
   * потребность через requestRate: кеш реактивен, после загрузки курса
   * геттер вызовется ещё раз и посчитает по-настоящему.
   */
  get newBestPrice(): Price {
    /** Если поставщик доставляет самостоятельно, deliveryPricePerUnit пустой и это окей, лучшая цена считается без него
     *  Если доставка платформой, то deliveryPricePerUnit обязательна. если её нет, то возвращаем пустую цену */
    if (this.deliveryType.id === 'byETP' && this.deliveryPricePerUnit.isEmpty() === true) {
      return Price.fromEmpty()
    }

    const targetCurrencyCode  = this.offerCurrencyCode;
    const dateForCurrencyRate = this.getDateForCurrencyRate();

    let sourcePrice: Price;
    let sourceCurrencyCode: string;

    if (this.tender.bestBid) {
      sourcePrice        = this.tender.bestBid.priceFullBuyerWithDeferredPayment!;
      sourceCurrencyCode = this.tender.bestBid.offerCurrencyCode;
    }
    else {
      sourcePrice        = this.tender.priceStartFull!;
      sourceCurrencyCode = this.tender.priceStartCurrencyCode;
    }

    Core.currencies.requestRate(sourceCurrencyCode, dateForCurrencyRate);
    Core.currencies.requestRate(TenderBid.BASE_STEP_CURRENCY_CODE, dateForCurrencyRate);

    const currentBestPrice = sourcePrice.convertSync(
      sourceCurrencyCode,
      targetCurrencyCode,
      dateForCurrencyRate,
    );

    const stepInTargetCurrency = Price.fromServer(TenderBid.BASE_STEP_USD).convertSync(
      TenderBid.BASE_STEP_CURRENCY_CODE,
      targetCurrencyCode,
      dateForCurrencyRate,
    );

    /** Округление ВНИЗ, а не по правилам типа: шаг должен реально опустить
     *  цену, а half-up мог бы вернуть её обратно к прежнему значению.
     *  Прежние Math.floor с умножением и делением на 1000 делали ровно это,
     *  но через float — здесь то же самое в Decimal, без артефактов. */
    const newBestPriceValue = currentBestPrice!.value
      .minus(stepInTargetCurrency!.value)
      .toDecimalPlaces(3, Decimal.ROUND_FLOOR);

    const newBestPrice = Core.utils.money.calculatePriceSellerFromPriceFullBuyer(
      Price.fromOperation(newBestPriceValue),
      this.vatRateSeller,
      this.vatRateBuyer!,
      this.isVatIncludedSeller!,
      this.priceMod.plus(this.priceModDeferredPayment),
      this.deliveryPricePerUnit,
      this.deliveryType,
    );

    /** На стейдже дотыкали до такой степени, что у нас в лучшая цена стала отрицательной. */
    if (newBestPrice.lessThan(0)) {
      return Price.zero();
    }
    else {
      return newBestPrice;
    }
  }

  /** Своих характеристик у ставки нет — сверяются оферные. */
  getCharacteristics(): OfferCharacteristic[] {
    return this.offer!.characteristics;
  }

  getPriceWithDeferredPaymentMismatch(): string | null {
    return super.getPriceWithDeferredPaymentMismatch(
      this.tender.priceStartFull,
      this.tender.priceStartCurrencyCode,
    );
  }

  /**
   * Добавляет к общим расхождениям цену. В предке этого не сделать: там
   * Tender импортирован типом, а instanceof замкнул бы круг импортов.
   */
  getMismatches(target: Demand | Tender): MismatchItem[] {
    const result = super.getMismatches(target);

    if (this.getPriceWithDeferredPaymentMismatch() !== null) {
      result.push({
        label:       'Цена покупателя с отсрочкой',
        valueSource: this.priceFullBuyerWithDeferredPayment?.format(this.offerCurrencyCode) ?? '--',
        valueTarget: this.tender.priceStartFull?.format(this.tender.priceStartCurrencyCode) ?? '--',
      });
    }

    return result;
  }

  calculatePlannedPaymentDateToSeller(): void {
    if (this.paymentTerm) {
      this.plannedPaymentDateToSeller = this.paymentTerm.getRelativePaymentDate(new DateSimple(), this._deliveryDays);
    }
  }

  calculatePlannedPaymentDateByBuyer(): void {
    if (Core.utils.isNotEmptyArray(this.tender.desiredPaymentTerms)) {
      // в ставке он один
      const desiredPaymentTerm = this.tender.desiredPaymentTerms[0];

      this.plannedPaymentDateByBuyer = desiredPaymentTerm.getRelativePaymentDate(new DateSimple(), this._deliveryDays);
    }
  }

  setValuesBasedOnSellerCompany(): void {
    /** Здесь специально обращаюсь напрямую к приватным полям, чтобы
     * потом один раз вызвать calculatePrice */
    if (this._sellerCompany?.isVatPayer) {
      if (typeof this.offer!.isVatIncluded === 'boolean') {
        this._isVatIncludedSeller = this.offer!.isVatIncluded;
      }
      else {
        this._isVatIncludedSeller = true;
      }

      if (this.offer!.vatRateSeller && this.offer!.vatRateSeller.isPositive()) {
        this._vatRateSeller = this.offer!.vatRateSeller;
      }
      else {
        this._vatRateSeller = TenderBid.DEFAULT_VAT_RATE;
      }

      if (
        this._vatRateSeller
        && this.vatRateBuyer
        && this._vatRateSeller.greaterThan(this.vatRateBuyer)
      ) {
        Core.showNotification.error('Ошибка расчета цены: НДС поставщика выше НДС покупателя');
        throw new Error('Ошибка расчета цены: НДС поставщика выше НДС покупателя');
      }
    } else {
      this._isVatIncludedSeller = null;
      this._vatRateSeller       = null;
    }
  }

  validatePreorderMinQty(value: Qty | null): string | null {
    let maxQty: Qty | undefined;

    if (this.qtyAvailable === null || this.qtyAvailable.isEmpty()) {
      maxQty = undefined;
    }
    else {
      maxQty = this.qtyAvailable;
    }

    return Core.utils.number.validateDesiredQty(
      value,
      this.packageVolume,
      maxQty,
      this.packageVolume,
      this.unit,
    );
  }

  validatePreorderMonthlyReserve(value: Qty | null): string | null {
    // код оказался идентичным
    return this.validatePreorderMinQty(value);
  }

  validate(product = this.tender.product): string[] {
    return super.validate(product);
  }

  /** Вызывается из предка: calculatePrices и сеттер deliveryType. */
  calculatePriceMod(): void {
    if (!this.tender.productCategory) {
      //Core.showNotification.error('Не можем рассчитать коэффициент - нет категории');
      return;
    }

    /** С появлением deliveryPricePerUnit мы больше не учитываем кэф priceModTransport */
    this.priceMod = this.tender.productCategory.priceModProfit;
  }

  get isMine(): boolean {
    return this.seller.id === Core.currentPartner.id;
  }

  /**
   * Можно ли править реквизиты ставки: только поставщику и только пока
   * тендер не окончен. Покупатель видит ставку в режиме просмотра.
   */
  get canChangeRequisites(): boolean {
    if (this.tender.isFinished === true) {
      return false;
    }
    else if (Core.currentPartner.isBuyer === true) {
      return false;
    }
    else {
      return true;
    }
  }

  /**
   * Равна ли полная цена покупателя цене другой ставки.
   *
   * Ставки одного тендера могут быть выставлены в разных валютах, поэтому
   * сравнивать напрямую можно только когда валюта совпадает. Иначе обе цены
   * приводятся к валюте тендера на дату его начала.
   *
   * Округление до 3 знаков вшито в Price, отдельный round10 перед сравнением
   * не нужен. Сравнение — equals: valueOf у Numeric бросает.
   */
  async isPriceFullBuyerEqual(anotherBid: TenderBid): Promise<boolean> {
    if (this.offerCurrencyCode === anotherBid.offerCurrencyCode) {
      return this.priceFullBuyerWithDeferredPayment!.equals(anotherBid.priceFullBuyerWithDeferredPayment!);
    }
    else {
      const currencyCode        = this.tender.priceStartCurrencyCode;
      const dateForCurrencyRate = this.getDateForCurrencyRate();

      const onePrice = await Core.utils.money.convert(
        this.priceFullBuyerWithDeferredPayment!,
        this.offerCurrencyCode,
        currencyCode,
        dateForCurrencyRate,
      );
      const twoPrice = await Core.utils.money.convert(
        anotherBid.priceFullBuyerWithDeferredPayment!,
        anotherBid.offerCurrencyCode,
        currencyCode,
        dateForCurrencyRate,
      );

      return onePrice.equals(twoPrice);
    }
  }

  get priceModTotal() {
    return this.priceMod.plus(this.priceModDeferredPayment);
  }
}

export default TenderBid;
